import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2, Edit2, GripVertical, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

/**
 * FormulaQuickEdit - Modal d'édition rapide d'une formule depuis le POS
 * Permet de modifier les informations principales et les choix disponibles
 */
const FormulaQuickEdit = ({
    api,
    formula,
    products = [],
    isOpen,
    onClose,
    onSave
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: 0,
        is_available: true,
        badge: '',
        steps: []
    });
    const [saving, setSaving] = useState(false);
    const [expandedStep, setExpandedStep] = useState(null);

    // Initialize form data when formula changes
    useEffect(() => {
        if (formula && isOpen) {
            setFormData({
                name: formula.name || '',
                description: formula.description || '',
                price: formula.price || 0,
                is_available: formula.is_available !== false,
                badge: formula.badge || '',
                meal_type: formula.meal_type || 'all',
                steps: formula.steps?.map(step => ({
                    name: step.name,
                    required: step.required !== false,
                    min_choices: step.min_choices || 1,
                    max_choices: step.max_choices || 1,
                    choices: step.choices?.map(c => ({
                        product_id: c.product_id,
                        product_name: c.product_name,
                        price_adjustment: c.price_adjustment || 0,
                        destination: c.destination || 'cuisine',
                        is_default: c.is_default || false
                    })) || []
                })) || [],
                available_supplements: formula.available_supplements || []
            });
            setExpandedStep(null);
        }
    }, [formula, isOpen]);

    // Update step field
    const updateStep = (stepIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => 
                idx === stepIndex ? { ...step, [field]: value } : step
            )
        }));
    };

    // Add choice to step
    const addChoiceToStep = (stepIndex, product) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
                if (idx !== stepIndex) return step;
                if (step.choices.some(c => c.product_id === product.id)) {
                    toast.info('Produit déjà ajouté');
                    return step;
                }
                return {
                    ...step,
                    choices: [...step.choices, {
                        product_id: product.id,
                        product_name: product.name,
                        price_adjustment: 0,
                        destination: product.destination || 'cuisine',
                        is_default: step.choices.length === 0
                    }]
                };
            })
        }));
    };

    // Remove choice from step
    const removeChoiceFromStep = (stepIndex, choiceIndex) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
                if (idx !== stepIndex) return step;
                return {
                    ...step,
                    choices: step.choices.filter((_, ci) => ci !== choiceIndex)
                };
            })
        }));
    };

    // Update choice
    const updateChoice = (stepIndex, choiceIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
                if (idx !== stepIndex) return step;
                return {
                    ...step,
                    choices: step.choices.map((choice, ci) => 
                        ci === choiceIndex ? { ...choice, [field]: value } : choice
                    )
                };
            })
        }));
    };

    // Add new step
    const addStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, {
                name: `Groupe ${prev.steps.length + 1}`,
                required: true,
                min_choices: 1,
                max_choices: 1,
                choices: []
            }]
        }));
        setExpandedStep(formData.steps.length);
    };

    // Remove step
    const removeStep = (stepIndex) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter((_, idx) => idx !== stepIndex)
        }));
    };

    // Save formula
    const handleSave = async () => {
        if (!formData.name) {
            toast.error('Le nom est requis');
            return;
        }
        if (formData.price <= 0) {
            toast.error('Le prix doit être supérieur à 0');
            return;
        }

        setSaving(true);
        try {
            await api.put(`/api/pos/formulas/${formula.id}`, formData);
            toast.success('Formule mise à jour');
            onSave?.();
            onClose();
        } catch (error) {
            console.error('Error saving formula:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Toggle availability
    const toggleAvailability = async () => {
        setSaving(true);
        try {
            const newAvailability = !formData.is_available;
            await api.put(`/api/pos/formulas/${formula.id}`, {
                is_available: newAvailability
            });
            setFormData(prev => ({ ...prev, is_available: newAvailability }));
            toast.success(newAvailability ? 'Formule activée' : 'Formule désactivée');
            onSave?.();
        } catch (error) {
            console.error('Error toggling availability:', error);
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !formula) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="formula-quick-edit-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Edit2 className="w-5 h-5 text-emerald-600" />
                        Édition Rapide: {formula.name}
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="flex-1 pr-2">
                    <div className="space-y-5 p-1">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label className="text-xs font-medium text-gray-500 mb-1">Nom de la formule</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className="h-10"
                                    data-testid="formula-name-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-medium text-gray-500 mb-1">Prix (F CFA)</Label>
                                <Input
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                    className="h-10"
                                    data-testid="formula-price-input"
                                />
                            </div>
                            <div className="flex items-end gap-4">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={formData.is_available}
                                        onCheckedChange={toggleAvailability}
                                        disabled={saving}
                                        data-testid="formula-availability-toggle"
                                    />
                                    <span className={cn(
                                        "text-sm font-medium",
                                        formData.is_available ? "text-emerald-600" : "text-red-600"
                                    )}>
                                        {formData.is_available ? 'Disponible' : 'Indisponible'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-medium text-gray-500 mb-1">Description (optionnel)</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="h-10"
                                placeholder="Description courte..."
                            />
                        </div>

                        <Separator />

                        {/* Steps */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-900">Groupes de choix</h3>
                                <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={addStep}
                                    className="h-8"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Groupe
                                </Button>
                            </div>

                            <div className="space-y-3">
                                {formData.steps.map((step, stepIdx) => (
                                    <div 
                                        key={stepIdx}
                                        className={cn(
                                            "border rounded-xl overflow-hidden transition-all",
                                            expandedStep === stepIdx 
                                                ? "border-emerald-400 bg-emerald-50/50" 
                                                : "border-gray-200 bg-white"
                                        )}
                                    >
                                        {/* Step Header */}
                                        <div 
                                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                                            onClick={() => setExpandedStep(expandedStep === stepIdx ? null : stepIdx)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <GripVertical className="w-4 h-4 text-gray-400" />
                                                <span className="font-medium">{step.name}</span>
                                                {step.required && (
                                                    <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">
                                                        Obligatoire
                                                    </Badge>
                                                )}
                                                <Badge variant="secondary" className="text-[10px]">
                                                    {step.choices.length} choix
                                                </Badge>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={(e) => { e.stopPropagation(); removeStep(stepIdx); }}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        {/* Expanded Content */}
                                        {expandedStep === stepIdx && (
                                            <div className="p-3 pt-0 border-t border-gray-100 space-y-3">
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div>
                                                        <Label className="text-xs text-gray-500">Nom</Label>
                                                        <Input
                                                            value={step.name}
                                                            onChange={(e) => updateStep(stepIdx, 'name', e.target.value)}
                                                            className="h-8 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-gray-500">Min</Label>
                                                        <Select
                                                            value={step.min_choices?.toString()}
                                                            onValueChange={(v) => updateStep(stepIdx, 'min_choices', parseInt(v))}
                                                        >
                                                            <SelectTrigger className="h-8 text-sm">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {[0,1,2,3].map(n => (
                                                                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-gray-500">Max</Label>
                                                        <Select
                                                            value={step.max_choices?.toString()}
                                                            onValueChange={(v) => updateStep(stepIdx, 'max_choices', parseInt(v))}
                                                        >
                                                            <SelectTrigger className="h-8 text-sm">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {[1,2,3,5,10].map(n => (
                                                                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    <Switch
                                                        checked={step.required}
                                                        onCheckedChange={(c) => updateStep(stepIdx, 'required', c)}
                                                    />
                                                    <span className="text-sm">Obligatoire</span>
                                                </div>

                                                {/* Choices */}
                                                <div>
                                                    <Label className="text-xs text-gray-500 mb-2 block">Produits</Label>
                                                    <div className="space-y-1.5">
                                                        {step.choices.map((choice, choiceIdx) => (
                                                            <div 
                                                                key={choiceIdx}
                                                                className="flex items-center gap-2 px-2 py-1.5 bg-white border rounded-lg"
                                                            >
                                                                <span className="flex-1 text-sm truncate">
                                                                    {choice.product_name}
                                                                </span>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="text-xs text-gray-400">+</span>
                                                                    <Input
                                                                        type="number"
                                                                        className="w-16 h-6 text-xs"
                                                                        value={choice.price_adjustment || ''}
                                                                        onChange={(e) => updateChoice(stepIdx, choiceIdx, 'price_adjustment', parseFloat(e.target.value) || 0)}
                                                                        placeholder="0"
                                                                    />
                                                                    <span className="text-xs text-gray-400">F</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => removeChoiceFromStep(stepIdx, choiceIdx)}
                                                                    className="text-red-400 hover:text-red-600 p-1"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    
                                                    {/* Add product */}
                                                    <Select
                                                        onValueChange={(productId) => {
                                                            const product = products.find(p => p.id === productId);
                                                            if (product) addChoiceToStep(stepIdx, product);
                                                        }}
                                                    >
                                                        <SelectTrigger className="h-8 text-sm mt-2">
                                                            <SelectValue placeholder="+ Ajouter un produit..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {products.slice(0, 30).map(product => (
                                                                <SelectItem key={product.id} value={product.id}>
                                                                    {product.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {formData.steps.length === 0 && (
                                    <div className="text-center py-6 text-gray-400 border border-dashed rounded-xl">
                                        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                                        <p className="text-sm">Aucun groupe de choix</p>
                                        <p className="text-xs">Ajoutez un groupe pour définir les options</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ScrollArea>

                <DialogFooter className="border-t pt-4 gap-2">
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        disabled={saving}
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid="save-formula-btn"
                    >
                        {saving ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FormulaQuickEdit;
